import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFirebaseAuth } from './hooks/useAuth';
import { useDraftState, archiveDraft, loadPreviousYearPrices, useArchivedDrafts } from './hooks/useDraftState';
import { useTeamLiveData } from './hooks/useTeamData';
import { useLocalManagerId } from './hooks/useLocalManagerId';
import { useLeagueId } from './hooks/useLeagueId';
import { useToast } from './lib/toast';
import { COMMISSIONER_IDS, DEFAULT_LEAGUE_ID, DRAFT_YEAR } from './firebase';
import {
  buildInitialDraftState, startAuction, placeBid, recordPass, finalizeSale,
  cancelAuction, undoLastPick, undoLastTrade, executeTrade, maxBidPower, nextBidderId,
  type TradeSpec,
} from './lib/draftLogic';
import { computeValuations } from './lib/valuation';
import type { DraftState } from './types';
import { teamFullName } from './constants/teams';
import { loadAdvisorSettings, saveAdvisorSettings, type AdvisorSettings } from './lib/aiAdvisor';
import { sounds } from './lib/soundEffects';

import { Header } from './components/Header';
import { LeagueSelectionScreen } from './components/LeagueSelectionScreen';
import { ManagerSelectionScreen } from './components/ManagerSelectionScreen';
import { ManagerCard } from './components/ManagerCard';
import { NominationArea } from './components/NominationArea';
import { BiddingArea } from './components/BiddingArea';
import { AvailableTeamsList } from './components/AvailableTeamsList';
import { DraftHistory } from './components/DraftHistory';
import { CommissionerTools } from './components/CommissionerTools';
import { ConfirmModal } from './components/ConfirmModal';
import { Toaster } from './components/Toaster';
import { TradeModal } from './components/TradeModal';
import { ArchiveHistory } from './components/ArchiveHistory';
import { CheatSheet } from './components/CheatSheet';
import { BiddingIntelStrip } from './components/BiddingIntelStrip';
import { TopBargains } from './components/TopBargains';
import { PortfolioLeaderboard } from './components/PortfolioLeaderboard';
import { AIWarRoomModal } from './components/AIWarRoomModal';
import { AISettingsModal } from './components/AISettingsModal';
import { Season2025RecapModal } from './components/Season2025RecapModal';
import { HISTORIC_2025_SALES } from './constants/season2025Recap';
import type { PriorSaleHistory } from './components/TeamScoutingCard';

interface ConfirmCfg {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
}

export default function App() {
  const { uid, loading: authLoading, error: authError } = useFirebaseAuth();
  const [leagueId, setLeagueId] = useLeagueId();
  const [localId, setLocalId] = useLocalManagerId();
  const { state, loading, error, writeState } = useDraftState(!!uid && !!leagueId, leagueId ?? DEFAULT_LEAGUE_ID);
  const { data: liveData, rankings, loading: dataLoading } = useTeamLiveData();
  const { addToast } = useToast();

  const [pastPrices, setPastPrices] = useState<Record<string, number>>({});
  const [pendingTeam, setPendingTeam] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmCfg | null>(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [warRoomOpen, setWarRoomOpen] = useState(false);
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const [recap2025Open, setRecap2025Open] = useState(false);
  const [advisorSettings, setAdvisorSettings] = useState<AdvisorSettings>(() => {
    const s = loadAdvisorSettings();
    if (localId) s.managerId = localId;
    return s;
  });
  const { archives, loading: archivesLoading, refresh: refreshArchives } = useArchivedDrafts(!!uid && !!leagueId, leagueId ?? DEFAULT_LEAGUE_ID);

  // Compute prior year sales for each team from 2025 static archive + Firestore archives
  const pastSalesByTeam = useMemo(() => {
    const map: Record<string, PriorSaleHistory[]> = {};
    for (const [abbr, entry] of Object.entries(HISTORIC_2025_SALES)) {
      map[abbr] = [{
        year: 2025,
        price: entry.price,
        winner: entry.winner,
        winnerId: entry.winnerId,
      }];
    }
    for (const arc of archives) {
      for (const h of arc.history || []) {
        if (!map[h.team]) map[h.team] = [];
        const existingIdx = map[h.team].findIndex(s => s.year === arc.year);
        const item: PriorSaleHistory = {
          year: arc.year,
          price: h.price,
          winner: h.winner,
          winnerId: h.winnerId,
        };
        if (existingIdx >= 0) {
          map[h.team][existingIdx] = item;
        } else {
          map[h.team].push(item);
        }
      }
    }
    for (const abbr in map) {
      map[abbr].sort((a, b) => b.year - a.year);
    }
    return map;
  }, [archives]);

  // Sync advisor settings target manager with active user
  useEffect(() => {
    if (localId && advisorSettings.managerId !== localId) {
      setAdvisorSettings(curr => ({ ...curr, managerId: localId }));
    }
  }, [localId, advisorSettings.managerId]);

  // Load previous year's prices on mount / league change
  useEffect(() => {
    if (!uid || !leagueId) return;
    loadPreviousYearPrices(DRAFT_YEAR - 1, leagueId).then(setPastPrices);
  }, [uid, leagueId]);

  // Clear local manager id when the league changes — manager ids aren't portable.
  useEffect(() => {
    setLocalId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  const localManager = useMemo(
    () => (state && localId ? state.managers.find(m => m.id === localId) ?? null : null),
    [state, localId],
  );

  // Commissioner check: prefer per-league list; fall back to env-configured ids (legacy default league).
  const isCommissioner = !!localId && (
    state?.commissionerIds?.length
      ? state.commissionerIds.includes(localId)
      : COMMISSIONER_IDS.includes(localId.toLowerCase())
  );
  const isCommissionerId = (id: string) => (
    state?.commissionerIds?.length
      ? state.commissionerIds.includes(id)
      : COMMISSIONER_IDS.includes(id.toLowerCase())
  );
  const activeBidderId = state ? nextBidderId(state) : null;

  const valuations = useMemo(
    () => state ? computeValuations(state, liveData, pastPrices) : {},
    [state, liveData, pastPrices],
  );
  const localMaxBid = state && localManager ? maxBidPower(state, localManager.id) : 0;

  // --- Handlers ---

  const safeWrite = useCallback(
    async (mut: (s: DraftState) => DraftState) => {
      try {
        await writeState(mut);
      } catch (e: any) {
        addToast(e?.message ?? 'Update failed', 'error');
      }
    },
    [writeState, addToast],
  );

  const handleNominate = useCallback(
    async (team: string, bid: number) => {
      if (!state || !localManager) return;
      const power = maxBidPower(state, localManager.id);
      if (bid < state.minBid || bid > power || !Number.isInteger(bid)) {
        addToast(`Invalid bid (1–${power})`, 'error');
        return;
      }
      sounds.playBidPlaced();
      await safeWrite(s => startAuction(s, team, bid, localManager.id));
      setPendingTeam(null);
      addToast(`Auction started: ${teamFullName(team)} @ $${bid}`, 'info');
    },
    [state, localManager, safeWrite, addToast],
  );

  const handleBid = useCallback(
    async (amount: number) => {
      if (!state || !localManager) return;
      if (activeBidderId !== localManager.id) {
        addToast("It's not your turn.", 'error');
        return;
      }
      const power = maxBidPower(state, localManager.id);
      if (amount <= state.auction.highBid || amount > power || !Number.isInteger(amount)) {
        addToast(`Bid must be ${state.auction.highBid + 1}–${power}`, 'error');
        return;
      }
      sounds.playBidPlaced();
      await safeWrite(s => placeBid(s, localManager.id, amount));
      addToast(`Bid placed: $${amount}`, 'info');
    },
    [state, localManager, activeBidderId, safeWrite, addToast],
  );

  const handlePass = useCallback(async () => {
    if (!state || !localManager) return;
    if (activeBidderId !== localManager.id) {
      addToast("It's not your turn.", 'error');
      return;
    }
    if (localManager.id === state.auction.highBidderId) {
      addToast('The high bidder cannot pass.', 'error');
      return;
    }
    await safeWrite(s => recordPass(s, localManager.id));
    addToast('You passed.', 'info');
  }, [state, localManager, activeBidderId, safeWrite, addToast]);

  const handleSell = useCallback(async () => {
    if (!state || !isCommissioner) return;
    if (!state.auction.highBidderId) {
      addToast('No high bidder yet.', 'error');
      return;
    }
    const winnerName = state.managers.find(m => m.id === state.auction.highBidderId)?.name;
    const team = state.auction.team;
    const price = state.auction.highBid;
    sounds.playSold();
    await safeWrite(s => finalizeSale(s));
    addToast(`${team} → ${winnerName} for $${price}!`, 'success');
  }, [state, isCommissioner, safeWrite, addToast]);

  const handleVeto = useCallback(() => {
    if (!state || !isCommissioner || state.phase !== 'Bidding') return;
    setConfirm({
      title: 'Veto live auction?',
      message: <>Cancel the auction for <strong>{state.auction.team}</strong>? The team returns to the pool with no money charged.</>,
      confirmLabel: 'Veto',
      onConfirm: async () => {
        await safeWrite(s => cancelAuction(s));
        setConfirm(null);
        addToast('Auction cancelled.', 'success');
      },
    });
  }, [state, isCommissioner, safeWrite, addToast]);

  const handleUndo = useCallback(() => {
    if (!state || !isCommissioner || state.history.length === 0) return;
    const last = state.history[0];
    setConfirm({
      title: 'Undo last pick?',
      message: <>Revert <strong>{last.team}</strong> → {last.winner} for ${last.price}. Budget refunded.</>,
      confirmLabel: 'Undo',
      onConfirm: async () => {
        await safeWrite(s => undoLastPick(s));
        setConfirm(null);
        addToast('Last pick undone.', 'success');
      },
    });
  }, [state, isCommissioner, safeWrite, addToast]);

  const handleReset = useCallback(() => {
    if (!state || !isCommissioner) return;
    setConfirm({
      title: 'Factory reset?',
      message: 'This wipes ALL picks and resets budgets to their initial values. Cannot be undone.',
      confirmLabel: 'Reset',
      onConfirm: async () => {
        await safeWrite(s => {
          const fresh = buildInitialDraftState(s.currentDraftYear, {
            leagueId: s.leagueId,
            leagueName: s.leagueName,
            commissionerIds: s.commissionerIds,
            rosterCap: s.rosterCap,
            minBid: s.minBid,
          });
          // Preserve manager identities + custom initial budgets
          fresh.managers = s.managers.map(m => ({
            ...m, budget: m.initialBudget, teams: [], teamsDrafted: 0,
          }));
          return fresh;
        });
        setConfirm(null);
        addToast('Draft reset.', 'success');
      },
    });
  }, [state, isCommissioner, safeWrite, addToast]);

  const handleArchive = useCallback(() => {
    if (!state || !isCommissioner) return;
    const archiveYear = state.currentDraftYear;
    setConfirm({
      title: `Archive ${archiveYear} & start ${archiveYear + 1}?`,
      message: <>Save the {archiveYear} results permanently and reset the board for the {archiveYear + 1} season.</>,
      confirmLabel: 'Archive & New Year',
      destructive: false,
      onConfirm: async () => {
        await archiveDraft(archiveYear, state, leagueId ?? DEFAULT_LEAGUE_ID);
        await safeWrite(s => {
          const fresh = buildInitialDraftState(archiveYear + 1, {
            leagueId: s.leagueId,
            leagueName: s.leagueName,
            commissionerIds: s.commissionerIds,
            rosterCap: s.rosterCap,
            minBid: s.minBid,
          });
          fresh.managers = s.managers.map(m => ({
            ...m, budget: m.initialBudget, teams: [], teamsDrafted: 0,
          }));
          return fresh;
        });
        // Refresh past prices for the new "previous year"
        loadPreviousYearPrices(archiveYear, leagueId ?? DEFAULT_LEAGUE_ID).then(setPastPrices);
        refreshArchives();
        setConfirm(null);
        addToast(`Archived ${archiveYear}. ${archiveYear + 1} draft is live!`, 'success');
      },
    });
  }, [state, isCommissioner, safeWrite, addToast, leagueId, refreshArchives]);

  const handleUpdateBudget = useCallback(
    async (mid: string, newBudget: number) => {
      if (!isCommissioner) return;
      await safeWrite(s => ({
        ...s,
        managers: s.managers.map(m =>
          m.id === mid ? { ...m, initialBudget: newBudget, budget: newBudget } : m,
        ),
      }));
      addToast('Budget updated.', 'success');
    },
    [isCommissioner, safeWrite, addToast],
  );

  const handleUpdateDraftYear = useCallback(
    async (newYear: number) => {
      if (!isCommissioner || !Number.isInteger(newYear) || newYear < 2020 || newYear > 2035) return;
      await safeWrite(s => ({
        ...s,
        currentDraftYear: newYear,
      }));
      addToast(`Season set to ${newYear}.`, 'success');
    },
    [isCommissioner, safeWrite, addToast],
  );

  const handleExecuteTrade = useCallback(
    async (spec: TradeSpec) => {
      if (!state || !isCommissioner) return;
      await safeWrite(s => executeTrade(s, spec));
      addToast('Trade executed.', 'success');
    },
    [state, isCommissioner, safeWrite, addToast],
  );

  const handleUndoTrade = useCallback(() => {
    if (!state || !isCommissioner || !state.trades || state.trades.length === 0) return;
    const last = state.trades[0];
    setConfirm({
      title: 'Undo last trade?',
      message: <>Reverse the trade between <strong>{last.managerAName}</strong> and <strong>{last.managerBName}</strong>.</>,
      confirmLabel: 'Undo Trade',
      onConfirm: async () => {
        await safeWrite(s => undoLastTrade(s));
        setConfirm(null);
        addToast('Trade reversed.', 'success');
      },
    });
  }, [state, isCommissioner, safeWrite, addToast]);

  const handleSaveSnapshot = useCallback(() => {
    if (!state || !isCommissioner) return;
    const year = state.currentDraftYear;
    setConfirm({
      title: `Save ${year} snapshot?`,
      message: <>Save the current rosters, trades, and history as the {year} archive (overwriting any previous {year} snapshot). The live draft is <strong>not</strong> reset.</>,
      confirmLabel: 'Save Snapshot',
      destructive: false,
      onConfirm: async () => {
        try {
          await archiveDraft(year, state, leagueId ?? DEFAULT_LEAGUE_ID);
          refreshArchives();
          addToast(`${year} snapshot saved.`, 'success');
        } catch (e: any) {
          addToast(e?.message ?? 'Save failed', 'error');
        }
        setConfirm(null);
      },
    });
  }, [state, isCommissioner, addToast, refreshArchives, leagueId]);

  // --- Render gates ---

  if (authError) return <FullError title="Auth Failed" message={authError} />;
  if (authLoading) return <FullSpinner />;

  // 1. Pick a league before anything else.
  if (!leagueId) {
    return <LeagueSelectionScreen onSelect={setLeagueId} sessionUid={uid} />;
  }

  if (loading || !state) return <FullSpinner />;

  if (error?.includes('insufficient permissions')) return <PermissionsHelp />;
  if (error?.includes('does not exist')) {
    return (
      <FullError
        title="League not found"
        message={`No league with code "${leagueId}" exists.`}
        action={<button onClick={() => setLeagueId(null)} className="btn-primary mt-4">Pick another league</button>}
      />
    );
  }

  if (!localId) {
    return (
      <ManagerSelectionScreen
        onSelect={setLocalId}
        claimedIds={[]}
        sessionUid={uid}
        managers={state.managers}
        leagueName={state.leagueName}
        onSwitchLeague={() => setLeagueId(null)}
      />
    );
  }

  if (!localManager) {
    // Their id no longer exists in state (e.g. after rename)
    return (
      <FullError
        title="Manager not found"
        message={`No manager with id "${localId}" exists in this league.`}
        action={<button onClick={() => setLocalId(null)} className="btn-primary mt-4">Pick again</button>}
      />
    );
  }

  const canNominate =
    state.phase === 'Nomination' &&
    localManager.teamsDrafted < state.rosterCap &&
    state.managers[state.currentNomineeIndex]?.id === localManager.id &&
    maxBidPower(state, localManager.id) >= state.minBid;

  return (
    <div className="min-h-screen p-4 md:p-6 max-w-[1500px] mx-auto">
      <Header
        state={state}
        localManager={localManager}
        isCommissioner={isCommissioner}
        onSwitchUser={() => setLocalId(null)}
        leagueId={leagueId}
        onSwitchLeague={() => setLeagueId(null)}
        onOpenWarRoom={() => setWarRoomOpen(true)}
        onOpenSettings={() => setAiSettingsOpen(true)}
        onOpenRecap2025={() => setRecap2025Open(true)}
      />

      {/* 2025 Champion & Season Recap Alert Banner */}
      <div className="bg-gradient-to-r from-amber-950/80 via-slate-900 to-amber-950/80 border border-amber-500/40 rounded-xl p-2.5 px-4 mb-4 flex flex-wrap items-center justify-between gap-2 shadow-md">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-xl">🏆</span>
          <div>
            <span className="font-black text-amber-400 mr-1.5">2025 Season Winner:</span>
            <span className="text-white font-extrabold">David (71-65)</span>
            <span className="text-slate-400 ml-1">· Rob 2nd (69-67) · Ryan 3rd (68-68) · Frank 4th (64-72)</span>
          </div>
        </div>
        <button
          onClick={() => setRecap2025Open(true)}
          className="text-xs px-2.5 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-black shadow transition active:scale-95"
        >
          View 2025 Recap & Best Bargains →
        </button>
      </div>

      {dataLoading && (
        <div className="text-xs text-slate-500 mb-3 text-center">Loading ESPN team data…</div>
      )}

      <BiddingIntelStrip state={state} localManagerId={localId} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {state.phase === 'Nomination' && (
            <NominationArea
              state={state}
              localManager={localManager}
              pastPrices={pastPrices}
              liveData={liveData}
              rankings={rankings}
              valuations={valuations}
              pendingTeam={pendingTeam}
              settings={advisorSettings}
              pastSalesByTeam={pastSalesByTeam}
              onCancelPending={() => setPendingTeam(null)}
              onConfirmNominate={handleNominate}
              onSelectPendingTeam={(team, startingBid) => {
                setPendingTeam(team);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onOpenWarRoom={() => setWarRoomOpen(true)}
            />
          )}
          {state.phase === 'Bidding' && (
            <BiddingArea
              state={state}
              localManager={localManager}
              isCommissioner={isCommissioner}
              pastPrices={pastPrices}
              liveData={liveData}
              rankings={rankings}
              valuations={valuations}
              settings={advisorSettings}
              pastSalesByTeam={pastSalesByTeam}
              onBid={handleBid}
              onPass={handlePass}
              onSell={handleSell}
              onVeto={handleVeto}
              onOpenWarRoom={() => setWarRoomOpen(true)}
              onOpenSettings={() => setAiSettingsOpen(true)}
            />
          )}
          {state.phase === 'Finished' && (
            <div className="panel p-8 border-2 border-emerald-500 text-center">
              <h2 className="text-4xl font-extrabold text-emerald-400 mb-2">🏆 DRAFT {state.currentDraftYear} COMPLETE 🏆</h2>
              <p className="text-slate-300">All {state.managers.length} managers filled their {state.rosterCap}-team rosters.</p>
              {isCommissioner && (
                <p className="text-sm text-slate-400 mt-3">Commissioner: use "Archive & New Year" to start the next season.</p>
              )}
            </div>
          )}

          {isCommissioner && (
            <CommissionerTools
              state={state}
              onUndoLastPick={handleUndo}
              onUndoLastTrade={handleUndoTrade}
              onVeto={handleVeto}
              onResetDraft={handleReset}
              onArchiveAndReset={handleArchive}
              onSaveSnapshot={handleSaveSnapshot}
              onOpenTrade={() => setTradeOpen(true)}
              onUpdateBudget={handleUpdateBudget}
              onUpdateDraftYear={handleUpdateDraftYear}
            />
          )}

          <div>
            <h2 className="text-xl font-extrabold text-white mb-3">Manager Dashboards</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {state.managers.map(m => (
                <ManagerCard
                  key={m.id}
                  state={state}
                  manager={m}
                  isLocalUser={m.id === localId}
                  isCommissioner={isCommissionerId(m.id)}
                  isActiveBidder={state.phase === 'Bidding' && activeBidderId === m.id}
                  liveData={liveData}
                  rankings={rankings}
                />
              ))}
            </div>
          </div>

          <PortfolioLeaderboard state={state} liveData={liveData} rankings={rankings} />

          <TopBargains
            state={state}
            valuations={valuations}
            pastPrices={pastPrices}
            liveData={liveData}
            canNominate={canNominate}
            onNominate={team => {
              setPendingTeam(team);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />

          <DraftHistory state={state} />

          <CheatSheet
            state={state}
            liveData={liveData}
            rankings={rankings}
            valuations={valuations}
            pastPrices={pastPrices}
            canNominate={canNominate}
            localMaxBid={localMaxBid}
            onNominate={team => {
              setPendingTeam(team);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />

          {/* Show trades inline if any */}
          {state.trades && state.trades.length > 0 && (
            <div className="panel p-4">
              <h2 className="text-lg font-extrabold text-white mb-3">
                🔄 {state.currentDraftYear} Trades <span className="text-slate-400 text-sm font-normal">({state.trades.length})</span>
              </h2>
              <ul className="space-y-1.5">
                {state.trades.map(t => (
                  <li key={t.id} className="text-xs bg-slate-800/60 rounded p-2 border-l-2 border-teal-500">
                    <span className="font-bold text-teal-300">{t.managerAName}</span>
                    {' '}sent{' '}
                    <span className="text-amber-300">{t.teamsFromA.join(', ') || '—'}</span>
                    {t.cashFromA ? <span className="text-emerald-400"> + ${t.cashFromA}</span> : null}
                    {' '}↔{' '}
                    <span className="text-amber-300">{t.teamsFromB.join(', ') || '—'}</span>
                    {t.cashFromB ? <span className="text-emerald-400"> + ${t.cashFromB}</span> : null}
                    {' '}from{' '}
                    <span className="font-bold text-teal-300">{t.managerBName}</span>
                    {t.note && <div className="text-slate-500 italic mt-0.5">"{t.note}"</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ArchiveHistory
            archives={archives}
            loading={archivesLoading}
            currentYear={state.currentDraftYear}
            onOpenRecap2025={() => setRecap2025Open(true)}
          />
        </div>

        <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]">
          <AvailableTeamsList
            state={state}
            pastPrices={pastPrices}
            liveData={liveData}
            rankings={rankings}
            valuations={valuations}
            canNominate={canNominate}
            onNominate={team => {
              setPendingTeam(team);
              // smooth-scroll up so the nominee can see the bid form
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          destructive={confirm.destructive ?? true}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {tradeOpen && (
        <TradeModal
          state={state}
          onClose={() => setTradeOpen(false)}
          onExecute={handleExecuteTrade}
        />
      )}

      {warRoomOpen && localManager && (
        <AIWarRoomModal
          state={state}
          manager={localManager}
          liveData={liveData}
          valuations={valuations}
          settings={advisorSettings}
          onClose={() => setWarRoomOpen(false)}
          onOpenSettings={() => {
            setWarRoomOpen(false);
            setAiSettingsOpen(true);
          }}
          onSelectTeamForNomination={team => {
            setPendingTeam(team);
            setWarRoomOpen(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {aiSettingsOpen && (
        <AISettingsModal
          settings={advisorSettings}
          onSave={newSettings => {
            setAdvisorSettings(newSettings);
            saveAdvisorSettings(newSettings);
            addToast('AI settings saved', 'info');
          }}
          onClose={() => setAiSettingsOpen(false)}
        />
      )}

      {recap2025Open && (
        <Season2025RecapModal onClose={() => setRecap2025Open(false)} />
      )}

      <Toaster />
    </div>
  );
}

function FullSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-teal-400 text-lg font-semibold animate-pulse">Loading…</div>
    </div>
  );
}

function FullError({ title, message, action }: { title: string; message: string; action?: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="panel p-8 max-w-lg border-2 border-rose-500 text-center">
        <h2 className="text-2xl font-bold text-rose-400 mb-2">{title}</h2>
        <p className="text-slate-300">{message}</p>
        {action}
      </div>
    </div>
  );
}

function PermissionsHelp() {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="panel p-8 max-w-2xl border-2 border-rose-500">
        <h2 className="text-3xl font-extrabold text-rose-400 mb-4">🚨 Firestore Access Denied</h2>
        <p className="text-slate-300 mb-4">
          The Commissioner must publish open Firestore rules so the app can read/write the draft document.
        </p>
        <ol className="list-decimal list-inside text-slate-300 space-y-2 text-sm">
          <li>
            Open the{' '}
            <a className="text-teal-400 underline" href={`https://console.firebase.google.com/project/${projectId}/firestore/rules`} target="_blank" rel="noreferrer">
              Firestore Rules console
            </a>
          </li>
          <li>Replace the rules with the contents of <code className="bg-slate-800 px-1.5 py-0.5 rounded">firestore.rules</code> from this repo</li>
          <li>Click <strong>Publish</strong></li>
        </ol>
      </div>
    </div>
  );
}
